import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  IRowMeta,
  removeSelectedPartitionAction,
  setSelectedPartitionAction,
} from '../../../redux/slices/oldBuilder';
import {
  IPartitionFieldsFragment,
  IPartitionValueFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import KubitContextualMenu from '../../Dropdown/ContextualMenu';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import { dropdownStyle } from '../../Dropdown/ContextualMenu/KubitContextualMenu';
import PartitionValuesDisplay from './PartitionValuesDisplay';
import ConversionWindowInput from './ConversionWindowInput';
import { isNil } from 'lodash';
import { MaybeNil } from '../../../utils/interfaces';
import {
  DAY_PARTITION_ERROR_MESSAGE,
  isDayPartition,
  isMoreThanOneDay,
} from './utils';
import ContextualMenuSubItemWrapper from '../../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import toastMessage from '../../../utils/toastMessage';
import { SingleSelectLabel } from '../../Dropdown/SingleSelect';
import ContextualMenuOption from '../../Dropdown/ContextualMenu/ContextualMenuOption';
import styled from 'styled-components';
import Radio from '../../Radio';
import { assertNotNil } from '../../../utils/types';
import TickCheckbox from '../../Checkbox';

interface IProps {
  selectedPartitions: IPartitionValueFieldsFragment[];
  availablePartitions: IPartitionFieldsFragment[];
  setPartitionValue: (
    value: IPartitionValueFieldsFragment,
    rowMeta?: IRowMeta,
  ) => void;
  isTimeToConvertMode: boolean;
}

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  width: 100%;
  height: 100%;
`;

export default ({
  selectedPartitions,
  availablePartitions,
  setPartitionValue,
  isTimeToConvertMode,
}: IProps) => {
  const dispatch = useDispatch();
  const noneValue = useMemo((): MaybeNil<IPartitionValueFieldsFragment> => {
    const nonePartition = availablePartitions.find(
      (p) => p.__typename === 'NonePartition',
    );
    return !isNil(nonePartition)
      ? {
          __typename: 'PartitionValue',
          partition: nonePartition,
          conversionWindow: null,
        }
      : null;
  }, [availablePartitions]);

  const isDayPartitionSelected = useMemo(() => {
    const dayPartition = selectedPartitions.find(({ partition }) =>
      isDayPartition(partition),
    );

    return !!dayPartition;
  }, [selectedPartitions]);

  // if there is a conversion window, and it is greater than one day, then we need to disable the day partition
  const conversionWindowGreaterThanOneDay = useMemo(() => {
    const conversionWindowPartition = selectedPartitions.find(
      ({ partition, conversionWindow }) =>
        partition.__typename === 'ConversionWindowPartition' &&
        conversionWindow &&
        conversionWindow.value,
    );

    if (conversionWindowPartition?.conversionWindow) {
      const { timeUnit, value } = conversionWindowPartition.conversionWindow;
      return isMoreThanOneDay(timeUnit, value);
    } else {
      return false;
    }
  }, [selectedPartitions]);

  const togglePartition = useCallback(
    (value: IPartitionValueFieldsFragment, checked: boolean) => {
      if (checked) {
        dispatch(
          removeSelectedPartitionAction({
            value,
            noneValue,
          }),
        );
      } else if (
        isDayPartition(value.partition) &&
        conversionWindowGreaterThanOneDay
      ) {
        toastMessage({ message: DAY_PARTITION_ERROR_MESSAGE, type: 'error' });
      } else {
        dispatch(
          setSelectedPartitionAction({
            value,
          }),
        );
      }
    },
    [conversionWindowGreaterThanOneDay, dispatch, noneValue],
  );

  const items = useMemo<IContextualMenuItem[]>(
    () =>
      availablePartitions.flatMap((partition) => {
        const result: IContextualMenuItem[] = [];
        const partitionValue = selectedPartitions?.find(
          (p) => p.partition.key === partition.key,
        );

        if (partition.__typename === 'NonePartition') {
          result.push(
            {
              key: partition.key,
              onRenderContent: () => (
                <ContextualMenuOption
                  onClick={() => {
                    togglePartition(
                      {
                        __typename: 'PartitionValue',
                        partition,
                        conversionWindow: null,
                      },
                      !!partitionValue,
                    );
                  }}
                  label={
                    <OptionContainer>
                      <Radio value={partition.key} checked={!!partitionValue} />
                      {partition.name}
                    </OptionContainer>
                  }
                />
              ),
            },
            {
              key: 'none-divider',
              itemType: ContextualMenuItemType.Divider,
            },
          );
        } else if (partition.__typename === 'FieldPartition') {
          result.push({
            key: partition.key,
            onRenderContent: (props) => (
              <ContextualMenuOption
                onClick={() => {
                  togglePartition(
                    {
                      __typename: 'PartitionValue',
                      partition,
                      conversionWindow: null,
                    },
                    !!partitionValue,
                  );

                  assertNotNil(props.dismissMenu);
                  props.dismissMenu(undefined, true);
                }}
                label={
                  <OptionContainer>
                    <TickCheckbox
                      value={partition.key}
                      checked={!!partitionValue}
                    />
                    {partition.name}
                  </OptionContainer>
                }
              />
            ),
          });
        } else if (partition.__typename === 'ConversionWindowPartition') {
          result.push({
            key: partition.key,
            onRenderContent: () => (
              <ContextualMenuOption
                hasSubMenus={true}
                label={
                  <OptionContainer>
                    <TickCheckbox
                      value={partition.key}
                      checked={!!partitionValue}
                    />
                    {partition.name}
                  </OptionContainer>
                }
              />
            ),
            subMenuProps: {
              styles: dropdownStyle,
              items: [
                {
                  key: 'conversion-window-input',
                  onRender: (_, dismissSubMenu) => (
                    <ContextualMenuSubItemWrapper>
                      <ConversionWindowInput
                        conversionWindow={partitionValue?.conversionWindow}
                        setConversionWindow={(duration) =>
                          setPartitionValue({
                            __typename: 'PartitionValue',
                            partition,
                            conversionWindow: duration,
                          })
                        }
                        // only close the submenu when click Apply conversion window
                        onApply={() => dismissSubMenu(undefined, false)}
                        isDayPartitionSelected={isDayPartitionSelected}
                        isTimeToConvertMode={isTimeToConvertMode}
                      />
                    </ContextualMenuSubItemWrapper>
                  ),
                },
              ],
            },
          });
        }
        return result;
      }),
    [
      availablePartitions,
      isDayPartitionSelected,
      isTimeToConvertMode,
      selectedPartitions,
      setPartitionValue,
      togglePartition,
    ],
  );

  return (
    <KubitContextualMenu
      items={items}
      trigger={
        <SingleSelectLabel isAlwaysActive={true}>
          <PartitionValuesDisplay partitionValues={selectedPartitions} />
        </SingleSelectLabel>
      }
    />
  );
};
